import React, { useCallback, useEffect, useState } from 'react';
import { debounce, throttle } from 'lodash';
import { useTranslate } from 'react-admin';

import { Grid } from '@material-ui/core';

import { SelectInput, TextInput } from '~/components';

import { createConsent } from '~/api/debitOrder';
import { Account, Amount } from '~/types';
import { ConsentHolder, RunTransferActionFormProps } from './RunTransferActionTypes';

import { l } from './RunTransferAction';
import AmountInput from '~/components/AmountInput';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import { useApiLocalization } from '~/hooks/useApiLocalization';
import { useTenant } from '~/hooks/tenant';

type DebitOrderFormProps = RunTransferActionFormProps;

const NO_CONSENT: ConsentHolder = {};

const DebitOrderForm = ({ resource, profileId, onConsent }: DebitOrderFormProps) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const [debitAccountNumber, setDebitAccountNumber] = useState<string | undefined>();
  const [debitAccount, setDebitAccount] = useState<Account | undefined>();
  const [recipientAccountNumber, setRecipientAccountNumber] = useState<string | undefined>();
  const [operationSubtype, setOperationSubtype] = useState<string | undefined>();
  const [referenceId, setReferenceId] = useState<string | undefined>();
  const [note, setNote] = useState<string | undefined>();
  const [amount, setAmount] = useState<Amount | null>();

  const debouncedSetNote = useCallback(debounce(setNote, 500), []);
  const debouncedSetReferenceId = useCallback(debounce(setReferenceId, 500), []);
  const debouncedSetRecipientAccountNumber = useCallback(
    debounce(setRecipientAccountNumber, 500),
    []
  );

  const additionalFilters = { type: 'REGULAR', status: 'ACTIVE' };

  const { data: tenant } = useTenant();
  const { data: handbook } = useHandbooksQuery(tenant?.id);
  const subtypes = (handbook?.operationSubtypes || [])
    .filter((s) => s.operationType === 'DEBIT_ORDER')
    .map((s) => s.operationSubtype);

  const { data: subtypeTranslations } = useApiLocalization('operationSubtype', subtypes.length > 0);

  const subtypeOptions = subtypes.map((subtype) => ({
    value: subtype,
    text: subtypeTranslations?.[`operationSubtype.${subtype}`] || subtype,
  }));

  const throttledMakeConsent = useCallback(
    throttle(
      (
        debitAccountNumber: string,
        recipientAccountNumber: string,
        operationSubtype: string,
        amount: Amount,
        note?: string,
        referenceId?: string
      ): any => {
        createConsent({
          sender: { accountNumber: debitAccountNumber },
          recipient: { accountNumber: recipientAccountNumber },
          operationSubtype,
          amount,
          note,
          referenceId: referenceId
            ? [{ id: 'DASHBOARD_REFERENCE', value: referenceId }]
            : undefined,
        })
          .then((response) => {
            onConsent({ consent: response.data });
          })
          .catch((e) => {
            onConsent({ error: e?.body?.code });
          });
      },
      1000,
      { leading: true }
    ),
    []
  );

  useEffect(() => {
    if (debitAccountNumber && recipientAccountNumber && operationSubtype && amount && note) {
      onConsent(NO_CONSENT);
      throttledMakeConsent(
        debitAccountNumber,
        recipientAccountNumber,
        operationSubtype,
        amount,
        note,
        referenceId
      );
    } else {
      onConsent(undefined);
    }
  }, [
    onConsent,
    throttledMakeConsent,
    debitAccountNumber,
    recipientAccountNumber,
    operationSubtype,
    amount,
    note,
    referenceId,
  ]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <SelectInput<string | undefined>
          options={subtypeOptions}
          label={t('operationSubtype')}
          value={operationSubtype}
          onChange={(e) => {
            setOperationSubtype(e.target.value);
            if (e.key === 'Enter') {
              e.stopPropagation();
            }
          }}
          style={{ width: 240 }}
          helperText=' '
          required
        />
      </Grid>
      <Grid item>
        <AccountTableSingleSelect
          resource={resource}
          profileId={profileId}
          label={t('debitAccountNumber')}
          value={debitAccountNumber}
          onChange={(account) => {
            setDebitAccount(account);
            setDebitAccountNumber(account.number);
          }}
          additionalFilters={additionalFilters}
          required
          chipVariant='balance'
          style={{ width: 240 }}
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('creditAccountNumber')}
          required
          onChange={(e: any) => {
            debouncedSetRecipientAccountNumber(e.target.value || undefined);
          }}
          style={{ width: 240 }}
        />
      </Grid>
      <Grid item>
        <AmountInput
          onChange={(newAmount) => {
            if (newAmount?.value !== amount?.value || newAmount?.currency !== amount?.currency) {
              setAmount(newAmount);
            }
          }}
          label={t('amount')}
          permanentSelectedCurrency={debitAccount?.currency}
          disabled={!debitAccount}
          currencyDisabled
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('comment')}
          style={{ width: 490 }}
          multiline
          required
          onChange={(e: any) => {
            debouncedSetNote(e.target.value || undefined);
          }}
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('reference')}
          style={{ width: 282 }}
          onChange={(e: any) => {
            debouncedSetReferenceId(e.target.value || undefined);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default DebitOrderForm;
